/**
 * A panel the trader names by its kind ("my scanner", "this heatmap") that is
 * not open, and what the chat model is told then (2026-10-18: on a layout with
 * no scanner, "Pick a stock with a big gap from my scanner" sent the chat model
 * to request_panel_selection for a scanner that could not exist; it waited out
 * the selection and asked the trader to pick one).
 *
 * The same kinds and words as the analyst's notice (edge-companion-api,
 * analyst/panel-evidence.ts), which cannot import this package at runtime; a
 * test there reads this file to keep the two in step. The watchlist, the chart and
 * the calendar are left out: tools read them without the panel.
 */
export const NAMED_PANEL_KINDS: ReadonlyArray<{
  kind: string
  re: RegExp
  /** The panel types that are it; null: any open panel is it. */
  types: readonly string[] | null
  /** What to add to get one. */
  add: string
}> = [
  {
    kind: 'scanner',
    re: /\b(?:my|this|that|our|the)\s+(?:multi-?)?scan(?:ner)?\b/i,
    types: ['scanner', 'multi-scanner'],
    add: 'a Scanner panel',
  },
  {
    kind: 'heatmap',
    re: /\b(?:my|this|that|our|the)\s+heat\s?map\b/i,
    types: ['sector-heatmap'],
    add: 'a Heatmap panel',
  },
  {
    kind: 'panel',
    re: /\b(?:my|this|that|our|the)\s+panel\b/i,
    types: null,
    add: 'the panel',
  },
  {
    kind: 'screen',
    re: /\bon\s+(?:my\s+|the\s+)?screen\b/i,
    types: null,
    add: 'a panel',
  },
]

/** The kinds `text` names that no open panel is. */
export function missingNamedPanels(text: string, panelTypes: readonly string[]): string[] {
  return NAMED_PANEL_KINDS.filter(
    (n) => n.re.test(text) && !panelTypes.some((t) => n.types === null || n.types.includes(t)),
  ).map((n) => n.kind)
}

/** The chat prompt section for kinds named that are not open. */
export function missingPanelSection(kinds: string[]): string {
  const named =
    kinds.length > 1 ? `${kinds.slice(0, -1).join(', ')} or ${kinds[kinds.length - 1]}` : kinds[0]
  const add = NAMED_PANEL_KINDS.filter((n) => kinds.includes(n.kind))
    .map((n) => n.add)
    .join(' or ')
  return (
    '## a panel the trader names is not open\n' +
    `There is no ${named} on this layout, so you cannot see one. Do not call ` +
    'request_panel_selection for it, never say that a ticker, price or row comes from it, and ' +
    'never put a market-wide list in its place. Tell the trader there is none here, and that ' +
    `they can switch to a layout that has one, or add ${add}; name no ticker from it.`
  )
}
